import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Eye, Loader2 } from 'lucide-react';
import { testPapersApi, TestPaper, TestPapersFilters } from '../../api/testPapers';
import { examsApi, ExamType } from '../../api/exams';
import { subjectsApi, Subject } from '../../api/subjects';

interface Props {
  isDark: boolean;
}

const ALL = '__all__';

/** DTM variants are generated, not stored — see docs/adr/0007. */
const DTM_SLUG = 'dtm';

export default function TestPapersPage({ isDark }: Props) {
  const [items, setItems] = useState<TestPaper[]>([]);
  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [filters, setFilters] = useState<TestPapersFilters>({});
  const [isLoading, setIsLoading] = useState(true);
  const [viewing, setViewing] = useState<TestPaper | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);

  useEffect(() => {
    Promise.all([examsApi.getExamTypes(), subjectsApi.getAll()])
      .then(([ets, subs]) => {
        setExamTypes(ets);
        setSubjects(subs);
      })
      .catch(() => {});
  }, []);

  const fetchItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await testPapersApi.getAll(filters);
      // DTM variants are no longer rows: they're generated on demand from the
      // subject bank (ADR-0007). The old DTM papers are still in the table —
      // completed sessions point at them — but they're retired and corrupt, so
      // showing them here would only invite someone to "fix" them. Sertifikat
      // and mavzuli still legitimately use test papers.
      setItems(data.filter((p) => p.exam_type?.slug !== DTM_SLUG));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch test papers');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleView = async (id: string) => {
    try {
      const paper = await testPapersApi.getById(id);
      setViewing(paper);
      setIsViewOpen(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch paper');
    }
  };

  const inputCls = cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200');
  const labelCls = cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500');
  const headCls = cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500');

  return (
    <div className="space-y-6">
      <div>
        <h1
          className={cn(
            'text-2xl lg:text-3xl font-bold tracking-tight',
            isDark ? 'text-white' : 'text-[#0F172A]',
          )}
        >
          Test Papers
        </h1>
        <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
          Imported variants per exam type. Use the import endpoint to load new ones.
        </p>
      </div>

      <div
        className={cn(
          'rounded-2xl border p-4 text-sm',
          isDark
            ? 'border-amber-800/50 bg-amber-950/20 text-amber-200'
            : 'border-amber-200 bg-amber-50 text-amber-800',
        )}
      >
        <span className="font-semibold">DTM variantlari bu yerda yo&apos;q.</span>{' '}
        DTM billeti endi saqlanmaydi — u fan bankidan generatsiya qilinadi
        (12 ta variant, har biri 90 savol / 189 ball). Savollarni o&apos;zgartirish
        uchun fan bankini tahrirlang. Batafsil: <code>docs/adr/0007</code>.
      </div>

      <Card
        className={cn(
          'rounded-2xl border-0 p-4',
          isDark ? 'bg-[#1E293B] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
        )}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label className={labelCls}>Exam type</Label>
            <Select
              value={filters.exam_type_slug || ALL}
              onValueChange={(v) =>
                setFilters((p) => ({ ...p, exam_type_slug: v === ALL ? undefined : v }))
              }
            >
              <SelectTrigger className={inputCls}>
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                {examTypes
                  .filter((et) => et.slug !== DTM_SLUG)
                  .map((et) => (
                    <SelectItem key={et.id} value={et.slug}>
                      {et.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* The "DTM direction" filter used to live here. It's gone with the
              DTM papers themselves — it would only ever return an empty list. */}

          <div className="space-y-1">
            <Label className={labelCls}>Subject</Label>
            <Select
              value={filters.subject_id || ALL}
              onValueChange={(v) =>
                setFilters((p) => ({ ...p, subject_id: v === ALL ? undefined : v }))
              }
            >
              <SelectTrigger className={inputCls}>
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                {subjects.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className={labelCls}>Year</Label>
            <Select
              value={filters.year ? String(filters.year) : ALL}
              onValueChange={(v) =>
                setFilters((p) => ({ ...p, year: v === ALL ? undefined : Number(v) }))
              }
            >
              <SelectTrigger className={inputCls}>
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                {[2025, 2024, 2023, 2022, 2021, 2020].map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card
        className={cn(
          'rounded-2xl border-0 overflow-hidden',
          isDark ? 'bg-[#1E293B] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
        )}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className={cn(isDark ? 'border-slate-700' : 'border-slate-100')}>
                <TableHead className={headCls}>Title</TableHead>
                <TableHead className={headCls}>Exam type</TableHead>
                <TableHead className={headCls}>Direction / Subject</TableHead>
                <TableHead className={headCls}>Year</TableHead>
                <TableHead className={headCls}>Variant</TableHead>
                <TableHead className={headCls}>Questions</TableHead>
                <TableHead className={headCls}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    No test papers
                  </TableCell>
                </TableRow>
              ) : (
                items.map((tp) => (
                  <TableRow
                    key={tp.id}
                    className={cn(
                      isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50',
                    )}
                  >
                    <TableCell className={cn('font-medium max-w-sm', isDark ? 'text-white' : 'text-[#0F172A]')}>
                      <div className="truncate">{tp.title}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {tp.exam_type?.slug || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                      {tp.dtm_direction?.name || tp.subject?.name || '—'}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{tp.year || '—'}</TableCell>
                    <TableCell className="text-sm text-slate-500">{tp.variant_number}</TableCell>
                    <TableCell className="text-sm text-slate-500">{tp.total_questions}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleView(tp.id)} className="h-8 px-2">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent
          className={cn(
            'max-w-4xl max-h-[90vh] overflow-y-auto',
            isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white',
          )}
        >
          <DialogHeader>
            <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>
              {viewing?.title || 'Test paper'}
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 py-2">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline">{viewing.exam_type?.slug}</Badge>
                {viewing.year && <Badge variant="outline">{viewing.year}</Badge>}
                <Badge variant="outline">variant {viewing.variant_number}</Badge>
                <Badge variant="outline">{viewing.total_questions} questions</Badge>
                {viewing.time_limit_minutes && <Badge variant="outline">{viewing.time_limit_minutes} min</Badge>}
              </div>
              {viewing.source_file && (
                <div className="text-xs text-slate-500">Source: {viewing.source_file}</div>
              )}
              <div className="space-y-2 max-h-[60vh] overflow-y-auto pt-2">
                {viewing.paper_questions?.map((pq, idx) => (
                  <div
                    key={pq.id}
                    className={cn(
                      'p-3 rounded text-sm',
                      isDark ? 'bg-slate-800' : 'bg-slate-50',
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <span className="font-bold text-slate-500 w-6">{idx + 1}.</span>
                      <div className="flex-1">
                        <div className={cn(isDark ? 'text-slate-200' : 'text-slate-800')}>
                          {pq.question?.text || '—'}
                        </div>
                        {pq.role && (
                          <Badge variant="outline" className="text-xs mt-1">
                            {pq.role}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
